import React, { useEffect, useMemo, useState } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"

const GROUP_ORDER = [
  "mastery",
  "present",
  "past",
  "future",
  "articles",
  "agreement",
  "prepositions",
  "comparatives",
  "conditionals",
  "modals",
  "reported",
  "passives",
]

function groupLabel(group, t) {
  return t(`grammarGroup_${group}`) || group
}

function formHeaders(topic, t) {
  if (Array.isArray(topic?.form_headers) && topic.form_headers.length === 4) {
    return topic.form_headers
  }
  return [t("grammarPerson"), t("grammarPositive"), t("grammarNegative"), t("grammarQuestion")]
}

function recommendedTopic(topics) {
  const ready = topics.filter((item) => item.ready)
  const openLesson = ready.find((item) => !item.completed && item.group !== "mastery")
  const openAny = ready.find((item) => !item.completed)
  const started = ready.some((item) => item.attempts > 0)
  if (!started) {
    return ready.find((item) => item.id === "present_simple") || openLesson || openAny
  }
  return openLesson || openAny || null
}

export default function Grammar() {
  const { t } = useLanguage()
  const [topics, setTopics] = useState([])
  const [summary, setSummary] = useState({ completed: 0, mastered: 0, total: 0 })
  const [topic, setTopic] = useState(null)
  const [answers, setAnswers] = useState({})
  const [result, setResult] = useState(null)
  const [missedIds, setMissedIds] = useState(null)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const loadTopics = () =>
    api.grammarTopics().then((data) => {
      setTopics(data.topics || [])
      setSummary({
        completed: data.completed ?? data.mastered ?? 0,
        mastered: data.mastered || 0,
        total: data.total || (data.topics || []).filter((item) => item.ready).length,
      })
      return data
    })

  useEffect(() => {
    let cancelled = false
    setError("")
    loadTopics()
      .catch((exc) => {
        if (!cancelled) setError(exc.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const grouped = useMemo(() => {
    const byGroup = {}
    topics.forEach((item) => {
      const key = item.group || "other"
      if (!byGroup[key]) byGroup[key] = []
      byGroup[key].push(item)
    })
    const extra = Object.keys(byGroup).filter((key) => !GROUP_ORDER.includes(key))
    return [...GROUP_ORDER, ...extra]
      .filter((group) => byGroup[group]?.length)
      .map((group) => ({ group, items: byGroup[group] }))
  }, [topics])

  const nextUp = useMemo(() => recommendedTopic(topics), [topics])

  const nextTopicAfterCurrent = useMemo(() => {
    if (!topic) return null
    const ready = topics.filter((item) => item.ready)
    const index = ready.findIndex((item) => item.id === topic.id)
    return (
      ready.slice(index + 1).find((item) => !item.completed) ||
      ready.find((item) => item.id !== topic.id && !item.completed) ||
      null
    )
  }, [topics, topic])

  const openTopic = async (id) => {
    setError("")
    setResult(null)
    setAnswers({})
    setMissedIds(null)
    setLoading(true)
    try {
      const data = await api.grammarTopic(id)
      setTopic(data)
    } catch (exc) {
      setError(exc.message)
    } finally {
      setLoading(false)
    }
  }

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
  }

  const checkAnswers = async (event) => {
    event.preventDefault()
    if (!topic || submitting) return
    setSubmitting(true)
    setError("")
    try {
      const scored = await api.completeGrammarTopic(topic.id, answers)
      setResult(scored)
      setMissedIds(null)
      loadTopics().catch(() => {})
    } catch (exc) {
      setError(exc.message)
    } finally {
      setSubmitting(false)
    }
  }

  const retryMissed = () => {
    const missed = result?.missed_ids || (result?.results || []).filter((row) => !row.correct).map((row) => row.id)
    if (!missed.length) return
    setMissedIds(missed)
    setAnswers((prev) => {
      const kept = { ...prev }
      missed.forEach((id) => {
        delete kept[id]
      })
      return kept
    })
    setResult(null)
  }

  const backToList = () => {
    setTopic(null)
    setResult(null)
    setAnswers({})
    setMissedIds(null)
    setError("")
  }

  const resultById = useMemo(() => {
    const map = {}
    for (const row of result?.results || []) map[row.id] = row
    return map
  }, [result])

  const visibleItems = useMemo(() => {
    const items = topic?.items || []
    if (!missedIds?.length) return items
    return items.filter((item) => missedIds.includes(item.id))
  }, [topic, missedIds])

  if (loading && !topic && topics.length === 0) {
    return (
      <div className="grammar-page">
        <p className="text-muted-foreground">{t("loading")}</p>
      </div>
    )
  }

  return (
    <div className="grammar-page">
      <header className="vocab-hero">
        <h1>{t("grammar")}</h1>
        <p>{t("grammarPageLead")}</p>
        {summary.total > 0 ? (
          <p className="grammar-progress-meta">
            {t("grammarMasteryCount")
              .replace("{n}", String(summary.completed ?? summary.mastered ?? 0))
              .replace("{total}", String(summary.total))}
          </p>
        ) : null}
      </header>

      {error ? (
        <p className="vocab-inline-error" role="alert">
          {error}
        </p>
      ) : null}

      {!topic ? (
        <>
          {nextUp ? (
            <section className="grammar-recommended">
              <p className="grammar-recommended-label">{t("grammarRecommended")}</p>
              <button type="button" className="grammar-topic-card grammar-topic-card--next" onClick={() => openTopic(nextUp.id)}>
                <strong>{nextUp.title}</strong>
                <span>{nextUp.summary}</span>
                <em>
                  {nextUp.difficulty} · {nextUp.item_count} {t("grammarItems")}
                </em>
              </button>
            </section>
          ) : null}
          {grouped.map(({ group, items }) => (
            <section key={group} className="grammar-group">
              <h2>{groupLabel(group, t)}</h2>
              <ul className="grammar-topic-list">
                {items.map((item) => (
                  <li key={item.id}>
                    {item.ready ? (
                      <button type="button" className="grammar-topic-card" onClick={() => openTopic(item.id)}>
                        <strong>
                          {item.title}
                          {item.mastered ? (
                            <span className="grammar-badge">{t("grammarMastered")}</span>
                          ) : item.completed ? (
                            <span className="grammar-badge">{t("grammarCompleted")}</span>
                          ) : null}
                        </strong>
                        <span>{item.summary}</span>
                        <em>
                          {item.difficulty} · {item.item_count} {t("grammarItems")}
                          {item.attempts > 0 && item.best_percent != null
                            ? ` · ${t("grammarBest")} ${Math.round(item.best_percent)}%`
                            : null}
                        </em>
                      </button>
                    ) : (
                      <div className="grammar-topic-card is-soon">
                        <strong>{item.title}</strong>
                        <span>{item.summary}</span>
                        <em>{t("grammarComingSoon")}</em>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      ) : (
        <section className="grammar-practice">
          <button type="button" className="vocab-remove" onClick={backToList}>
            {t("grammarBackToTopics")}
          </button>
          <h2>{topic.title}</h2>
          {topic.explanation ? <p className="grammar-explain">{topic.explanation}</p> : null}
          {topic.form?.length ? (
            <div className="grammar-form-wrap">
              <table className="grammar-form">
                <thead>
                  <tr>
                    {formHeaders(topic, t).map((header) => (
                      <th key={header}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topic.form.map((row) => (
                    <tr key={row.person}>
                      <td>{row.person}</td>
                      <td>{row.positive}</td>
                      <td>{row.negative}</td>
                      <td>{row.question}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {topic.examples?.length ? (
            <ul className="grammar-examples">
              {topic.examples.map((example) => (
                <li key={example.sentence}>
                  <strong>{example.sentence}</strong>
                  {example.note ? <span>{example.note}</span> : null}
                </li>
              ))}
            </ul>
          ) : null}

          {result ? (
            <p className="vocab-inline-ok" role="status">
              {t("grammarScore")}: {result.score}/{result.total}
              {result.mastered ? ` · ${t("grammarMastered")}` : null}
            </p>
          ) : missedIds?.length ? (
            <p className="grammar-hint">{t("grammarRetryMissedHint")}</p>
          ) : null}

          <form className="grammar-items" onSubmit={checkAnswers}>
            {visibleItems.map((item, index) => {
              const scored = resultById[item.id]
              const answerLabel = Array.isArray(scored?.answers) ? scored.answers[0] : ""
              return (
                <fieldset key={item.id} className={scored ? (scored.correct ? "is-correct" : "is-wrong") : undefined}>
                  <legend>
                    {index + 1}. {item.prompt}
                  </legend>
                  {item.type === "multiple_choice" ? (
                    <ul className="grammar-options">
                      {(item.options || []).map((option) => (
                        <li key={option}>
                          <label>
                            <input
                              type="radio"
                              name={item.id}
                              value={option}
                              disabled={Boolean(result)}
                              checked={(answers[item.id] || "") === option}
                              onChange={() => setAnswer(item.id, option)}
                            />
                            {option}
                          </label>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <input
                      type="text"
                      className="vocab-search"
                      value={answers[item.id] || ""}
                      disabled={Boolean(result)}
                      onChange={(event) => setAnswer(item.id, event.target.value)}
                      autoComplete="off"
                      aria-label={item.prompt}
                    />
                  )}
                  {item.hint && !result ? <p className="grammar-hint">{t("grammarHint")}: {item.hint}</p> : null}
                  {scored && !scored.correct && answerLabel ? (
                    <p className="grammar-answer">
                      {t("grammarAnswer")}: {answerLabel}
                    </p>
                  ) : null}
                  {scored?.explanation ? (
                    <p className="grammar-why">
                      {t("grammarExplanation")}: {scored.explanation}
                    </p>
                  ) : null}
                </fieldset>
              )
            })}
            {result ? (
              <div className="grammar-actions">
                {result.missed_ids?.length ? (
                  <button type="button" className="vocab-btn vocab-btn--primary" onClick={retryMissed}>
                    {t("grammarRetryMissed")}
                  </button>
                ) : null}
                <button type="button" className="vocab-btn" onClick={() => openTopic(topic.id)}>
                  {t("grammarTryAgain")}
                </button>
                {nextTopicAfterCurrent ? (
                  <button type="button" className="vocab-btn" onClick={() => openTopic(nextTopicAfterCurrent.id)}>
                    {t("grammarNextTopic")}: {nextTopicAfterCurrent.title}
                  </button>
                ) : null}
              </div>
            ) : (
              <button type="submit" className="vocab-btn vocab-btn--primary" disabled={submitting}>
                {submitting ? t("loading") : t("grammarCheck")}
              </button>
            )}
          </form>
        </section>
      )}
    </div>
  )
}
